import { fromJS, Map as IMap, List as IList } from 'immutable';
import { Big } from 'big.js';
import { createSelector } from 'reselect';

import { E_CARD_TYPE, PHYSICAL_CARD_TYPE } from './productCartModule';
import { getIsBuyNowFlow } from '../routing/routingSelectors';
import { CARD_TYPES, CURRENT_ACCESSORY } from '../constants/constants';
import { getShippingSummary } from '../shipping/shippingSelectors';
import { getCartMaxNumPlasticCards, getGreetingsPrice } from '../brand/brandSelectors';
import { getConsumerFees } from '../fees/consumerFeeSelectors';
import { feeTypes, getFeeAmountByTypeAndCost } from '../fees/consumerFeeUtils';
import { getAccessoriesPromoData, getDiscountRewardAmount } from '../promo/sharedCartPromoSelectors';
import { getAccessoryCostByCurrency } from '../accessories/accessoriesSelectors';

export const getAddedProducts = state => state.get('addedProducts', IMap());

export const getOrderNumber = createSelector(
    getAddedProducts,
    addedProducts => addedProducts.get('orderNumber', '')
);


/**
 * Get the selected currency code
 * @param {Immutable.Map} state The current state.
 * @returns {String} The currency code for which the cards are selected
 *
 */
export const getCurrencyCode = createSelector(
    getAddedProducts,
    addedProducts => addedProducts.get('currencyCode', '')
);

/**
 * Get the digital items from the products added to cart includes removed items also
 * @param {Immutable.Map} state The current state.
 * @returns {Immutable.List} The list of items which are digital/e-gift cards
 *
 */
export const getAllEGiftCards = createSelector(
    getAddedProducts,
    cards => cards.get(E_CARD_TYPE, IList())
);

export const getAllPhysicalCards = createSelector(
    getAddedProducts,
    cards => cards.get(PHYSICAL_CARD_TYPE, IList())
);


// Helper function to exclude PrintAtHome cards
const excludePrintAtHome = items => items.filterNot(item => item.get('itemCardType') === CARD_TYPES.PrintAtHome);

export const getAllEGiftCardsExcludingPrintAtHome = createSelector(
    getAllEGiftCards,
    cards => excludePrintAtHome(cards)
);

export const getAllPrintAtHomeCards = createSelector(
    getAllEGiftCards,
    cards => cards.filter(activeProduct => activeProduct.get('itemCardType') === CARD_TYPES.PrintAtHome)
);

export const getActiveEGiftCards = createSelector(
    getAllEGiftCards,
    cards => cards.filterNot(activeProduct => activeProduct.get('removed'))
);

export const getActivePrintAtHomeCards = createSelector(
    getAllPrintAtHomeCards,
    cards => cards.filterNot(activeProduct => activeProduct.get('removed'))
);

export const getActivePhysicalCards = createSelector(
    getAllPhysicalCards,
    cards => cards.filterNot(activeProduct => activeProduct.get('removed'))
);

export const getAllActiveCards = createSelector(
    getActiveEGiftCards, getActivePhysicalCards,
    (digital, physical) => IList(digital.concat(physical))
);

export const getBuyNowEGiftCards = createSelector(
    getAddedProducts,
    (addedProducts) => {
        const buyNowItems = fromJS(addedProducts).get('buyNowItem', IMap());
        const eGiftCards = buyNowItems.get(E_CARD_TYPE, IList());
        return eGiftCards;
    }
);

export const getGreetingCount = createSelector(
    getAllEGiftCardsExcludingPrintAtHome,
    getBuyNowEGiftCards,
    getIsBuyNowFlow,
    (cards, buyNowCards, isBuyNow) => {
        const selectedCards = isBuyNow ? buyNowCards : cards;

        
        const uniqueTokens = selectedCards.reduce((acc, card) => {
            if (!card || card.get('removed')) return acc;

            const token = card.getIn(['greetings', 'token'], '');
            if (!token) return acc;

            acc.add(token);
            return acc;
        }, new Set());

        return uniqueTokens.size;
    }
);

/**
 * Get the Buy Now Physical card items from the products added to cart
 * @param {Immutable.Map} state The current state.
 * @returns {Immutable.List} The list of items which are Buy Now cards
 *
 */
export const getBuyNowPhysicalGiftCards = createSelector(
    getAddedProducts,
    (addedProducts) => {
        const buyNowItems = fromJS(addedProducts).get('buyNowItem', IMap());
        const physicalGiftCards = buyNowItems.get(PHYSICAL_CARD_TYPE, IList());
        return physicalGiftCards;
    }
);

const getFilteredEGiftCards = (isBuyNow, buyNowCards, cartCards, includePrintAtHome) => {
    if (isBuyNow) return buyNowCards;
    if (!includePrintAtHome) return excludePrintAtHome(cartCards);
    return cartCards;
};

const getFilteredPhysicalGiftCards = (isBuyNow, buyNowCards, cartCards) => {
    if (isBuyNow) return buyNowCards;
    return cartCards;
};

export const getPaymentEGiftCards = createSelector(
    [getIsBuyNowFlow, getBuyNowEGiftCards, getActiveEGiftCards],
    (isBuyNow, buyNowCards, cartCards) => getFilteredEGiftCards(isBuyNow, buyNowCards, cartCards, true)
);

export const getPaymentPhysicalGiftCards = createSelector(
    [getIsBuyNowFlow, getBuyNowPhysicalGiftCards, getActivePhysicalCards],
    (isBuyNow, buyNowCards, cartCards) => getFilteredPhysicalGiftCards(isBuyNow, buyNowCards, cartCards)
);

export const getConfirmationEGiftCards = createSelector(
    [getIsBuyNowFlow, getBuyNowEGiftCards, getActiveEGiftCards],
    (isBuyNow, buyNowCards, cartCards) => getFilteredEGiftCards(isBuyNow, buyNowCards, cartCards, false)
);

export const isPromoItem = item => (
    item && item.has('category') && item.get('category') === 'PRMO'
);

export const filterPaidItems = items => (
    items.filter(item => !isPromoItem(item))
);

export const getPaidItems = createSelector(
    getAllActiveCards,
    filterPaidItems
);


export const getItemDenomination = item => (
    parseFloat(item && item.get('cardValue'))
);

export const getItemQuantity = item => (
    item && item.get('quantity', 1)
);

export const getItemTotalCost = item => (
    new Big(getItemDenomination(item)).times(getItemQuantity(item)) || new Big('0')
);

export const sumOfItemQuantity = items => (
    items.reduce((acc, item) => (
        acc + getItemQuantity(item)
    ), 0)
);




export const itemHasAccessory = item => !!item.get(CURRENT_ACCESSORY);

export const getItemAccessory = (item) => {
    const accessory = item.get(CURRENT_ACCESSORY, null);

    if (!accessory) {
        return IMap();
    }

    return fromJS(accessory);
};


export const getActiveItems = createSelector(
    getAllActiveCards,
    cards => cards
);

export const getItemsWithAccessories = createSelector(
    [getIsBuyNowFlow, getBuyNowEGiftCards, getBuyNowPhysicalGiftCards, getAllActiveCards],
    (isBuyNow, buyNowEGiftCards, buyNowPhysicalGiftCards, allActiveCards) => {
        const sourceItems = isBuyNow ? buyNowEGiftCards.concat(buyNowPhysicalGiftCards) : allActiveCards;
        return sourceItems.filter(itemHasAccessory);
    }
);

export const getItemsAccessoryCount = createSelector(
    getItemsWithAccessories,
    items => items.reduce((acc, item) => acc + getItemQuantity(item), 0)
);

export const getItemAccessoryTotalCost = (item, accessoryPromoData, currencyCode) => {
    if (!itemHasAccessory(item)) {
        return new Big('0');
    }

    const accessory = getItemAccessory(item);
    const quantity = getItemQuantity(item);
    const accessoryCost = getAccessoryCostByCurrency(accessory, accessoryPromoData, currencyCode);

    return new Big(accessoryCost || 0).times(quantity);
};

export const getItemAccessoriesGrandTotalCost = createSelector(
    getItemsWithAccessories, getAccessoriesPromoData, getCurrencyCode,
    (items, accessoryPromoData, currency) => items.reduce(
        (acc, item) => acc.add(getItemAccessoryTotalCost(item, accessoryPromoData, currency)),
        new Big('0')
    )
);

export const getDigitalCardQuantityExcludingPrintAtHome = createSelector(
    getConfirmationEGiftCards,
    digitalItems => (
        sumOfItemQuantity(digitalItems)
    )
);


export const getPrintAtHomeCardQuantity = createSelector(
    getActivePrintAtHomeCards,
    digitalItems => (
        sumOfItemQuantity(digitalItems)
    )
);


export const getPhysicalCardQuantity = createSelector(
    getPaymentPhysicalGiftCards,
    physicalItems => physicalItems.reduce((sum, item) => sum + (item.get('quantity') || 0), 0)
);

export const sumOfFaceValues = items => (
    items.reduce((acc, item) => (
        new Big(acc).add(new Big(getItemDenomination(item)).times(getItemQuantity(item)))
    ), 0)
);

export const getSubTotal = createSelector(
    [getPaymentEGiftCards, getPaymentPhysicalGiftCards],
    (eGiftCards, physicalGiftCards) => {
        const allCards = eGiftCards.concat(physicalGiftCards); 
        const filteredCards = allCards.filter(item => !isPromoItem(item)); 
        return new Big(sumOfFaceValues(filteredCards)); 
    }
);
export const getTotalCardQuantity = createSelector(
    getAllActiveCards,
    activeCards => sumOfItemQuantity(activeCards)
);

export const getCartCurrencyCode = createSelector(
    [getTotalCardQuantity, getCurrencyCode],
    (totalCardsInCart, currencyCode) => (
        totalCardsInCart >= 1 ? currencyCode : ''
    )
);

/**
 * Get the activation active cards
 * @param {Immutable.Map} state The current state.
 * @returns {String} getTotalActiveCards
 *
 */
export const getTotalActiveCards = createSelector(
    getAddedProducts,
    addedProducts => addedProducts.get('totalActiveCards', 0)
);

export const getActivationFeeTotal = createSelector(
    getAddedProducts,
    addedProducts => addedProducts.get('activationFeeTotal', 0)
);

export const getActivationFeeCount = createSelector(
    getAddedProducts,
    addedProducts => addedProducts.get('activationFeeCount', 0)
);

export const getEGreetingCardTotal = createSelector(
    getGreetingCount,
    getGreetingsPrice,
    (greetingCount, greetingsPrice) => greetingCount * greetingsPrice
);

export const getGrandTotalCostMinusFees = createSelector(
    [getSubTotal, getDiscountRewardAmount],
    (totalCost, discount) => new Big(totalCost).add(discount)
);

export const getGrandTotalCostWithFees = createSelector(
    [
        getGrandTotalCostMinusFees,
        getActivationFeeTotal,
        getShippingSummary,
        getItemAccessoriesGrandTotalCost,
        getEGreetingCardTotal
    ],
    (grandTotalWithoutFees, activationFeeTotal, shippingSummary, accessoriesTotal, eGreetingCardTotal) => {
        const shippingCost = shippingSummary ? parseFloat(shippingSummary.totalCost) : 0;
        return new Big(grandTotalWithoutFees)
            .add(activationFeeTotal)
            .add(shippingCost)
            .add(accessoriesTotal)
            .add(eGreetingCardTotal);
    }
);

export const getGrandTotal = createSelector(
    getAddedProducts,
    addedProducts => addedProducts.get('grandTotal', 0.00)
);

export const getItemRecipients = createSelector(
    getActiveEGiftCards,
    getActivePhysicalCards,
    (eGiftCards, physicalCards) => {
        const recipients = {};

        
        const processCard = (item, isPhysical = false) => {
            const itemId = item.get('id');
            if (!itemId) return;

            const selfBuy = item.get('selfBuy');
            const recipientEmail = item.get('recipientEmail');
            const yourName = item.get('yourName');
            const yourEmail = item.get('yourEmail');
            const senderName = item.get('senderName');
            const senderEmail = item.get('senderEmail');

            const shippingAddress = item.get('shippingAddress');
            const recipientName = (isPhysical && !selfBuy)
                ? (shippingAddress && shippingAddress.get('fullName'))
                : item.get('recipientName');

            
            if (recipientEmail && recipientName) {
                recipients[`${itemId}_recipient`] = {
                    email: recipientEmail,
                    recipient_name: recipientName
                };
            }

            
            const finalSenderEmail = selfBuy ? yourEmail : senderEmail;
            const finalSenderName = selfBuy ? yourName : senderName;

            if (finalSenderEmail && finalSenderName) {
                recipients[`${itemId}_sender`] = {
                    email: finalSenderEmail,
                    recipient_name: finalSenderName
                };
            }
        };

        
        eGiftCards.forEach(item => processCard(item));
        physicalCards.forEach(item => processCard(item, true));

        return fromJS(recipients);
    }
);
export const getCardRecipients = createSelector(
    [getActiveEGiftCards, getActivePhysicalCards],
    (eGiftCards, physicalCards) => {
        const safeEGiftCards = eGiftCards || IList();
        const safePhysicalCards = physicalCards || IList();

        const processCard = (recipients, card) => {
            if (!card) return recipients;

            const cardId = card.get('id');
            if (!cardId) return recipients;

            const isPhysical = card.get('itemCardType') === 'Physical';
            let isValidRecipient = false;
            let recipientName;

            
            if (isPhysical) {
                const shippingAddress = card.get('shippingAddress');
                recipientName = shippingAddress && shippingAddress.get('fullName');
                isValidRecipient = card.has('recipientEmail') && recipientName;
            } else {
                recipientName = card.get('recipientName');
                isValidRecipient = card.has('recipientEmail') && recipientName;
            }

            
            if (isValidRecipient) {
                return recipients.set(cardId, {
                    email: card.get('recipientEmail'),
                    recipient_name: recipientName
                });
            }

            return recipients;
        };

        let recipients = IMap();

        
        if (safeEGiftCards.size > 0) {
            recipients = safeEGiftCards.reduce(processCard, recipients);
        }

        
        if (safePhysicalCards.size > 0) {
            recipients = safePhysicalCards.reduce(processCard, recipients);
        }

        return recipients;
    }
);


export const getPaidItemsCount = createSelector(
    getPaidItems,
    items => items.size
);

export const getCardsCount = items => (
    items.reduce((acc, item) => (acc + getItemQuantity(item)), 0)
);


export const getPaidPlasticItems = createSelector(
    getActivePhysicalCards,
    filterPaidItems
);

export const getPaidPlasticCardsCount = createSelector(
    getPaidPlasticItems,
    items => getCardsCount(items)
);

export const getRemainingPlasticCardsAllowed = createSelector(
    getCartMaxNumPlasticCards,
    getPaidPlasticCardsCount,
    (cartMaxNumPlasticCards, paidPlasticCardsCount) => {
        const cartMaxPlastic = cartMaxNumPlasticCards || 100;
        const remainingPlasticCardsAllowed = cartMaxPlastic - paidPlasticCardsCount;
        if (remainingPlasticCardsAllowed < 0) {
            return 0;
        }
        return remainingPlasticCardsAllowed;
    }
);

export const getActivationFeeforDigitalAndPhysical = createSelector(
    getPaymentEGiftCards,
    getPaymentPhysicalGiftCards,
    getConsumerFees,
    (digitalItems, physicalItems, fees) => {
        let physicalFeeTotal = 0;
        let digitalFeeTotal = 0;
        let physicalactivationFeeCount = 0;
        let digitalactivationFeecount = 0;

        
        digitalItems.forEach((item) => {
            if (item && !item.get('removed')) {
                const amount = Number(item.get('cardValue'));
                const quantity = item.get('quantity') || 1;
                const activationFeeAmount = getFeeAmountByTypeAndCost(
                    fees.toJS ? fees.toJS() : fees,
                    feeTypes.DIGITAL_ACTIVATION,
                    amount
                );
                if (activationFeeAmount) {
                    digitalactivationFeecount += quantity;
                    digitalFeeTotal += (quantity * activationFeeAmount);
                }
            }
        });

        
        physicalItems.forEach((item) => {
            if (item && !item.get('removed')) {
                const amount = Number(item.get('cardValue'));
                const quantity = item.get('quantity') || 1;
                const activationFeeAmount = getFeeAmountByTypeAndCost(
                    fees.toJS ? fees.toJS() : fees,
                    feeTypes.PLASTIC_ACTIVATION,
                    amount
                );
                if (activationFeeAmount) {
                    physicalactivationFeeCount += quantity;
                    physicalFeeTotal += (quantity * activationFeeAmount);
                }
            }
        });

        return {
            physicalFeeTotal,
            digitalFeeTotal,
            physicalactivationFeeCount,
            digitalactivationFeecount
        };
    }
);

export const getActivationFee = (state, amount, feeType) => {
    const consumerFees = getConsumerFees(state);

    const activationFeeAmount = getFeeAmountByTypeAndCost(
        consumerFees.toJS ? consumerFees.toJS() : consumerFees,
        feeType,
        amount
    );

    return activationFeeAmount;
};

export const getCartResumeDone = (state) => {
    const value = state.getIn(['addedProducts', 'cartResumeDone'], null);
    return value === null ? null : Boolean(value);
};
